'use client'

import { CxpExtendedContactForm } from '@/components/common/cxp/cxp-extended-contact-form'
import {
    ResponsiveModal,
    ResponsiveModalContent,
} from '@/components/extension/responsive-modal'
import { Button } from '@/components/ui/button'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import Link from 'next/link'
import { useState } from 'react'

const DEFAULT_LOGO_IMAGE = '/image/placeholder-logo-cxp-white.png'

const DEFAULT_SOLUTION_OPTIONS_IDS = [
  'fast_uniform',
  'custom_production',
  'coolgift',
]

const DEFAULT_QUANTITY_OPTIONS_IDS = [
  'range_1_5',
  'range_6_19',
  'range_20_50',
  'range_51_100',
  'range_101_500',
  'range_501_1000',
  'above_1000',
]

const DEFAULT_CONTACT_INFO_TYPES = ['hotline', 'email'] as const
const CONTACT_VALUES = {
  hotline: { value: '1900 272 735', href: 'tel:1900272735' },
  email: { value: 'pod@coolmate.me', href: 'mailto:pod@coolmate.me' },
}

const DEFAULT_SOCIAL_LINKS = [
  {
    platform: 'zalo',
    href: 'http://zalo.me/0373649650',
    image: '/image/May2023/mceclip0_49.png',
    alt: 'zalo-pod',
  },
  {
    platform: 'facebook',
    href: 'https://www.facebook.com/cxpinaotheoyeucau/',
    image: '/image/April2023/mceclip1_8.png',
    alt: 'fb-pod',
  },
  {
    platform: 'youtube',
    href: 'https://www.youtube.com/@coolmate',
    image: '/image/placeholder-youtube.png',
    alt: 'youtube',
  },
  {
    platform: 'instagram',
    href: 'https://www.instagram.com/coolmate.vn/',
    image: '/image/placeholder-instagram.png',
    alt: 'instagram',
  },
]

// Helper to render icon based on contact type
const renderContactIcon = (type: 'hotline' | 'email') => {
  if (type === 'hotline') {
    return (
      <svg viewBox='0 0 24 24' fill='currentColor' className='h-full w-full'>
        <path d='M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z' />
      </svg>
    )
  }
  return (
    <svg viewBox='0 0 24 24' fill='currentColor' className='h-full w-full'>
      <path d='M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z' />
    </svg>
  )
}

interface FloatingContactButtonProps {
  text?: string
  isActive?: boolean
  solutionOptions?: Array<{ value: string; label: string; order: number }>
  quantityOptions?: Array<{ value: string; label: string; order: number }>
  formHeading?: string
  formBadgeText?: string
  logoImage?: string
  description?: string
  contactInfo?: Array<{
    type: 'hotline' | 'email'
    label: string
    value: string
    href: string
  }>
  socialLinks?: Array<{
    platform: string
    href: string
    image: string
    alt: string
    is_active?: boolean
    order?: number
  }>
}

export function FloatingContactButton({
  text,
  isActive = true,
  solutionOptions,
  quantityOptions,
  formHeading,
  formBadgeText,
  logoImage = DEFAULT_LOGO_IMAGE,
  description,
  contactInfo,
  socialLinks = DEFAULT_SOCIAL_LINKS,
}: FloatingContactButtonProps) {
  const t = useTranslations('cxp.home.contact_form')
  const tCommon = useTranslations('cxp.common')
  const [isOpen, setIsOpen] = useState(false)

  // Default values
  const displayFormHeading = formHeading || t('form_heading')
  const displayFormBadgeText = formBadgeText || t('form_badge_text')
  const displayDescription = description || t('description')
  const displayText = text || tCommon('contact_consultation')

  // Get contact info from API or use default
  const displayContactInfo = contactInfo?.length
    ? contactInfo
    : DEFAULT_CONTACT_INFO_TYPES.map((type) => ({
        type,
        label: t(`contact_info.${type}`),
        value: CONTACT_VALUES[type].value,
        href: CONTACT_VALUES[type].href,
      }))

  // Get solution options from API or use default
  const displaySolutionOptions = solutionOptions?.length
    ? solutionOptions
    : DEFAULT_SOLUTION_OPTIONS_IDS.map((id, index) => ({
        value: id,
        label: t(`solution_options.${id}`),
        order: index,
      }))

  // Get quantity options from API or use default
  const displayQuantityOptions = quantityOptions?.length
    ? quantityOptions
    : DEFAULT_QUANTITY_OPTIONS_IDS.map((id, index) => ({
        value: id,
        label: t(`quantity_options.${id}`),
        order: index,
      }))

  // Filter and sort social links
  const filteredSocialLinks =
    socialLinks
      ?.filter((link) => link.is_active !== false)
      .sort((a, b) => (a.order || 0) - (b.order || 0)) || DEFAULT_SOCIAL_LINKS

  if (!isActive) {
    return null
  }

  return (
    <>
      <Button
        onClick={() => setIsOpen(true)}
        className='fixed bottom-4 right-4 z-50 flex animate-shake-pulse items-center gap-2 rounded-md bg-[#ff4e00] px-3 shadow-lg transition-colors hover:bg-[#e64600] sm:bottom-8 sm:right-8 sm:gap-3 md:px-4 md:py-6'
        ga-tracking-value='CXP_floating_contact_button'
        ga-tracking-label='CXP_floating_contact_button'
        variant='primary'
      >
        <span className='font-formaDjr text-lg font-semibold normal-case text-white sm:text-xl'>
          {displayText}
        </span>
        <div className='relative flex h-10 w-10 items-center justify-center sm:h-[34px] sm:w-[34px]'>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            viewBox='0 0 24 24'
            fill='white'
            className='h-full w-full'
          >
            <path d='M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z' />
          </svg>
        </div>
      </Button>

      <ResponsiveModal open={isOpen} onOpenChange={setIsOpen}>
        <ResponsiveModalContent
          side='bottom'
          className='max-h-[90vh] overflow-y-auto rounded-t-xl border-none p-0 sm:rounded-xl lg:w-[80vw] lg:max-w-[80vw]'
        >
          <div className='grid grid-cols-1 gap-6 sm:gap-8 md:gap-10 lg:grid-cols-2 lg:gap-16'>
            {/* Left Side - Contact Info */}
            <div className='flex flex-col space-y-5 rounded-xl bg-[#1a1a1a] p-5 sm:space-y-6 sm:p-6 md:space-y-8 md:p-8 lg:rounded-r-none lg:p-12'>
              {/* Logo */}
              <div className='relative h-12 w-36 sm:h-[60px] sm:w-[180px] md:h-[79px] md:w-[223px]'>
                <Image
                  src={ImageHelper.getImageUrl(logoImage)}
                  alt='CXP Logo'
                  fill
                  className='object-contain'
                  sizes='(max-width: 640px) 144px, (max-width: 768px) 180px, 223px'
                />
              </div>

              {/* Description */}
              <p className='max-w-lg font-sans text-sm text-white sm:text-base md:text-lg'>
                {displayDescription}
              </p>

              <div className='flex flex-1 flex-col justify-end'>
                {/* Contact Info */}
                {displayContactInfo.length > 0 && (
                  <div className='space-y-3 sm:space-y-4 md:space-y-6'>
                    {displayContactInfo.map((info) => (
                      <div
                        key={info.type}
                        className='flex items-center gap-2.5 sm:gap-3 md:gap-4'
                      >
                        <div className='flex h-9 w-9 items-center justify-center rounded-full bg-neutral-100 text-neutral-700 sm:h-10 sm:w-10 md:h-12 md:w-12'>
                          <div className='h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6'>
                            {renderContactIcon(info.type)}
                          </div>
                        </div>
                        <div>
                          <p className='mb-0.5 font-sans text-[10px] text-neutral-300 sm:mb-1 sm:text-xs md:text-sm'>
                            {info.label}
                          </p>
                          <a
                            href={info.href}
                            className='text-sm font-semibold text-white transition-colors hover:text-[#ff4e00] sm:text-base md:text-lg'
                          >
                            {info.value}
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Social Links */}
              {filteredSocialLinks.length > 0 && (
                <div className='flex items-center justify-end gap-2.5 sm:gap-3 md:gap-4'>
                  {filteredSocialLinks.map((link, index) => (
                    <Link
                      key={link.platform || index}
                      href={link.href}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='flex h-9 w-9 items-center justify-center transition-colors sm:h-10 sm:w-10 md:h-12 md:w-12'
                    >
                      <Image
                        src={ImageHelper.getImageUrl(link.image)}
                        alt={link.alt}
                        width={24}
                        height={24}
                        className='h-full w-full object-contain'
                      />
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Right Side - Contact Form */}
            <CxpExtendedContactForm
              solutionOptions={displaySolutionOptions}
              quantityOptions={displayQuantityOptions}
              formHeading={displayFormHeading}
              formBadgeText={displayFormBadgeText}
            />
          </div>
        </ResponsiveModalContent>
      </ResponsiveModal>
    </>
  )
}
